/**
 * Shared Theme Defaults
 *
 * Configurations that are identical across all themes.
 * Extract to reduce duplication while preserving theme flexibility.
 */

import type { ThemeConfig } from "../types";

/**
 * Default vignette configuration - same across all themes
 */
export const DEFAULT_VIGNETTE: ThemeConfig["vignette"] = {
  opacity: 0.8,
  gradientSteps: 4,
  gradientWidth: 50,
  gradientCurve: "ease-out",
};

/**
 * Default POI minimum zoom levels by tier - same across all themes
 */
export const DEFAULT_POI_MIN_ZOOM: ThemeConfig["poiMinZoom"] = {
  1: 16, // Entrance, attraction, animal
  2: 17, // Restroom, food, shop, emergency
  3: 18, // Utilities, playground, artwork, viewpoint
};

/**
 * Default POI name label config — ENABLED by default (plan 118 option A locked).
 * Halo (soft, via baked `text-halo-blur: 1.5` in the layer factory) provides
 * readability. Tier minzooms govern label visibility. Per-POI override on
 * `pois.show_name_label`.
 */
export const DEFAULT_POI_LABEL: NonNullable<ThemeConfig["poiLabel"]> = {
  enabled: true,
  placement: "below",
  fontSize: 11,
  color: "#1f2937",
  haloColor: "rgba(255,255,255,0.9)",
  haloWidth: 1.5,
};

/**
 * Default prop configurations - same across all themes
 * iconSize for 64px normalized sprites
 * zOrder: 7.5-7.9 range (below bridge/vignette)
 */
export const DEFAULT_PROPS: ThemeConfig["props"] = {
  // Decorative props
  tree: { icon: "prop-tree", iconSize: 0.55, iconOpacity: 1, minZoom: 14, zOrder: 7.8 },
  "tree-palm": { icon: "prop-palm", iconSize: 0.55, iconOpacity: 1, minZoom: 14, zOrder: 7.8 },
  "tree-pine": { icon: "prop-pine", iconSize: 0.55, iconOpacity: 1, minZoom: 14, zOrder: 7.8 },
  lamp: { icon: "prop-lamp", iconSize: 0.35, iconOpacity: 1, minZoom: 15, zOrder: 7.9 },
  bush: { icon: "prop-bush", iconSize: 0.45, iconOpacity: 1, minZoom: 14, zOrder: 7.6 },
  rock: { icon: "prop-rock", iconSize: 0.4, iconOpacity: 1, minZoom: 14, zOrder: 7.5 },
};
